<!-- This version of the communities dropdown allows users to select from any clickable area -->
<!-- It relies on more native components and vuetify props -->

<template>
  <v-select
    v-scroll="handleScroll"
    flat
    chips
    clearable
    open-on-clear
    variant="outlined"
    density="comfortable"
    color="darker-grey"
    bg-color="darker-grey"
    item-color="$primary-700"
    base-color="darker-grey"
    persistent-placeholder
    placeholder="SELECT COMMUNITY"
    :items="communityTitles"
    v-model="selectedCommunity"
    prepend-inner-icon="mdi-tune-variant"
    class="communities_select_dropdown weg_translate">
  </v-select>
</template>

<script lang="ts" setup>
import router from '@/router';
import { computed, ref, watch } from 'vue';
import { useUserDataStore } from '@/store/userDataStore';

const userDataStore = useUserDataStore();

// Computed property to get the community titles and hrefs
const communityData = computed(() => {
  const myCommunities = userDataStore.menuData.site_map['My communities'];
  return myCommunities ? myCommunities.href : {};
});

// Computed property to get the community titles only (for dropdown)
const communityTitles = computed(() => ['All', ...Object.keys(communityData.value)]);

// Selected community title, initialized with a default value
const selectedCommunity = ref(null);

watch(selectedCommunity, (newValue) => {
  let href = '';
  if (newValue === 'All') {
    href = '/dashboard?community&community';
  } else if (newValue && communityData.value[newValue]) {
    href = '/' + communityData.value[newValue].href;
  }

  if (href) {
    navigateToUrl(href);
  }
});

function navigateToUrl(href: string | null) {
  if (href) {
    router.push(href);
  }
}

// function to hide the communities results when the navbar slides out of view on scroll
const scrollPosition = ref(0);
const handleScroll = () => {
  const currentScrollPosition = window.scrollY;
  const communitiesSelect = document.querySelector('.v-select__content');
  if (currentScrollPosition > 299 && currentScrollPosition > scrollPosition.value && communitiesSelect) {
    communitiesSelect!.classList.add('hide-select');
  } else if (communitiesSelect) {
    communitiesSelect!.classList.remove('hide-select');
  }
  scrollPosition.value = window.scrollY;
};
</script>

<style lang="scss">
/* Style the Placeholder Element */
// Hide the Input form title when it moves to the top of the box on active
.communities_select_dropdown {
  width: 100%;
  border: solid 1px $primary-700;
  border-radius: 5px;

  // Style the placeholder text and ensure it is centered and full width
  .v-label.v-field-label {
    width: 100%;
    @include v_chip_text;
  }

  .v-field__input {
    opacity: 1;
  }

  // Style the placeholder background
  .v-field__input input {
    background: transparent;
  }

  .v-field--active {
    .v-label.v-field-label {
      visibility: hidden;
      display: none;
    }
  }
}

/* Style the Prepend Icon */
// Update style for Slider icon on communities filter and ensure it is centered
.communities_select_dropdown {
  .mdi-tune-variant {
    opacity: 1 !important;
    color: $primary-700;
  }

  .mdi-tune-variant::before {
    opacity: 1 !important;
    color: $primary-700;
  }
}

/* Style the dropdown menu items/chips */
// Ensure the "Placeholder" is centered in the input form and increase all internal elements to 100% width
.communities_select_dropdown {
  .v-field__input {
    display: flex;
    flex-wrap: wrap;
    flex-direction: column;
    align-content: center;
    justify-content: center;
    align-items: center;
    padding: 0px;

    input::placeholder {
      text-align: center;
      color: $primary-700;
      opacity: 1;
      font-family: $brandon-grotesque-font;
      font-weight: bold;
      font-size: 13px;
      letter-spacing: 2px;
    }
  }

  // Ensure the chip selection is full width
  .v-select__selection {
    width: 100% !important;
  }

  // Ensure the chip is centered and full width
  .v-chip {
    width: 100% !important;
    @include display_flex_center;
    margin-left: 10px;
  }

  // Style the text inside the chip to match Zeplin
  .v-chip__content {
    @include v_chip_text;
  }
}

//* Dropdown Arrow Styling *//
// Update style for dropdown arrow on communities filter and ensure it is centered
.communities_select_dropdown {
  .mdi-menu-down {
    opacity: 1 !important;
  }

  .mdi-menu-down::before {
    color: $site-purple;
    font-size: 32px;
    opacity: 1 !important;
  }

  .v-field__append-inner {
    display: flex;
    padding: 0px !important;
    flex-direction: column;
    align-content: center;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
  }
}

//* Vuetify Element Styling *//
// Hides the extra element below the search/select form that creates a random space
.communities_select_dropdown {
  .v-input__details {
    display: none !important;
  }
}

// Fix to reduce the height of the dropdown menu items. This needs to be addressed in the refactor - Luke
.v-list-item--density-default.v-list-item--one-line {
  min-height: 38px;
  padding-top: 4px;
  padding-bottom: 4px;
}

.v-overlay-container .v-overlay__content.v-select__content {
  transition: transform 0.2s ease;
  top: 55px !important;

  &.hide-select {
    transform: translateY(-600px);
  }
}
</style>
